import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface Props {
  /** Anchor id — a `/readme/<slug>#<id>` deep-link opens + scrolls to this. */
  id: string;
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

/**
 * An anchor-addressable expandable within a manual section page (TRIL-424).
 * When the URL hash matches this section's `id`, it auto-expands, scrolls into
 * view, and moves focus to its control — the mechanism behind a help icon that
 * deep-links to a specific part of a page (e.g. `/readme/vouching#revoke`).
 */
export default function ManualSection({
  id,
  title,
  defaultOpen = false,
  children,
}: Props) {
  const { hash } = useLocation();
  const isTarget = hash === `#${id}`;
  const [open, setOpen] = useState(defaultOpen || isTarget);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const bodyId = `${id}-body`;

  useEffect(() => {
    if (!isTarget) return;
    setOpen(true);
    ref.current?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    // Land keyboard/AT focus on the section's control, not wherever it was.
    buttonRef.current?.focus({ preventScroll: true });
  }, [isTarget]);

  return (
    <div
      ref={ref}
      id={id}
      className={`MANUAL__EXPANDABLE${open ? ' is-open' : ''}`}
    >
      <button
        ref={buttonRef}
        type="button"
        className="MANUAL__EXPANDABLE__HEAD"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{title}</span>
        <ChevronDown
          className="MANUAL__EXPANDABLE__CHEV"
          size={18}
          aria-hidden="true"
        />
      </button>
      <div id={bodyId} className="MANUAL__EXPANDABLE__BODY" hidden={!open}>
        {children}
      </div>
    </div>
  );
}
